import React, { useState, useEffect } from 'react';
import { Button, ListGroup, Form, Row, Col, Spinner, Card, Modal } from 'react-bootstrap';
import { ArrowLeft } from 'react-bootstrap-icons';

const API_URL = process.env.REACT_APP_API_URL;

const emptyQualification = { university: '', course: '', year: '' };
const emptyExperience = { company: '', role: '', from: '', to: '' };

const ConsultantTab = () => {
  const [consultants, setConsultants] = useState([]);
  const [view, setView] = useState('list'); // 'list', 'detail', 'applicant'
  const [selected, setSelected] = useState(null); // selected consultant
  const [loading, setLoading] = useState(false);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null); // applicant object from applicants list

  // For create/edit
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  // For edit
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);

  // For delete
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state (for both create and edit)
  const [form, setForm] = useState({
    name: "",
    email: "",
    skills: "",
    position: "",
    rate: "",
    availability: "",
    qualification: [ {...emptyQualification} ],
    experience: [ {...emptyExperience} ],
    projects: "",
    // No cv
  });

  useEffect(() => {
    fetchConsultants();
    // eslint-disable-next-line
  }, []);

  const fetchConsultants = async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/admin/consultants`);
    const data = await res.json();
    setConsultants(data);
    setLoading(false);
  };

  // Show consultant detail and fetch their applicants
  const handleShowDetail = async (consultant) => {
    setSelected(consultant);
    setView('detail');
    setApplicants([]);
    setSelectedApplicant(null);
    setLoadingApplicants(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/consultant-applicants/${consultant._id}`);
      const data = await res.json();
      setApplicants(data);
    } catch {
      setApplicants([]);
    }
    setLoadingApplicants(false);
  };

  // Show applicant detail, using the applicant object from applicants[]
  const handleShowApplicantDetail = (app) => {
    setSelectedApplicant(app);
    setView('applicant');
  };

  // Edit Consultant
  const handleEditClick = () => {
    setForm({
      name: selected.name || "",
      email: selected.email || "",
      skills: selected.skills || "",
      position: selected.position || "",
      rate: selected.rate || "",
      availability: selected.availability || "",
      qualification: selected.qualification && selected.qualification.length > 0
        ? selected.qualification.map(q => ({...q}))
        : [ {...emptyQualification} ],
      experience: selected.experience && selected.experience.length > 0
        ? selected.experience.map(e => ({...e}))
        : [ {...emptyExperience} ],
      projects: selected.projects ? (Array.isArray(selected.projects) ? selected.projects.join(', ') : selected.projects) : "",
    });
    setShowEdit(true);
  };

  const handleDeleteClick = () => setShowDelete(true);

  // For add/edit consultant logic
  const handleQualChange = (i, field, value) => {
    setForm(f => ({
      ...f,
      qualification: f.qualification.map((q, idx) => idx === i ? {...q, [field]: value } : q)
    }));
  };
  const addQual = () => setForm(f => ({ ...f, qualification: [...f.qualification, {...emptyQualification}] }));
  const removeQual = (i) => setForm(f => ({
    ...f,
    qualification: f.qualification.length > 1 ? f.qualification.filter((_, idx) => idx !== i) : f.qualification
  }));

  const handleExpChange = (i, field, value) => {
    setForm(f => ({
      ...f,
      experience: f.experience.map((e, idx) => idx === i ? {...e, [field]: value } : e)
    }));
  };
  const addExp = () => setForm(f => ({ ...f, experience: [...f.experience, {...emptyExperience}] }));
  const removeExp = (i) => setForm(f => ({
    ...f,
    experience: f.experience.length > 1 ? f.experience.filter((_, idx) => idx !== i) : f.experience
  }));

  // CREATE (POST) - keep as FormData for now if backend expects it, otherwise convert to JSON (let me know if needed)
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    // We keep this as FormData in case your create API still expects it
    let formData = new FormData();
    let projectsArr = [];
    if (form.projects) {
      try { projectsArr = JSON.parse(form.projects); }
      catch { projectsArr = form.projects.split(',').map(x => x.trim()).filter(Boolean); }
    }
    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("skills", form.skills);
    formData.append("position", form.position);
    formData.append("rate", form.rate);
    formData.append("availability", form.availability);
    formData.append("qualification", JSON.stringify(form.qualification.filter(q => q.university || q.course || q.year)));
    formData.append("experience", JSON.stringify(form.experience.filter(e => e.company || e.role || e.from || e.to)));
    formData.append("projects", JSON.stringify(projectsArr));
    // No CV

    try {
      const res = await fetch(`${API_URL}/api/admin/consultants`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        setShowCreate(false);
        setForm({
          name: "",
          email: "",
          skills: "",
          position: "",
          rate: "",
          availability: "",
          qualification: [ {...emptyQualification} ],
          experience: [ {...emptyExperience} ],
          projects: "",
        });
        fetchConsultants();
      }
    } finally {
      setCreating(false);
    }
  };

  // EDIT (PUT) - send JSON only, no FormData or files
  const handleEdit = async (e) => {
    e.preventDefault();
    setEditing(true);
    let projectsArr = [];
    if (form.projects) {
      try { projectsArr = JSON.parse(form.projects); }
      catch { projectsArr = form.projects.split(',').map(x => x.trim()).filter(Boolean); }
    }
    const body = {
      name: form.name,
      email: form.email,
      skills: form.skills,
      position: form.position,
      rate: form.rate,
      availability: form.availability,
      qualification: form.qualification.filter(q => q.university || q.course || q.year),
      experience: form.experience.filter(e => e.company || e.role || e.from || e.to),
      projects: projectsArr,
    };
    try {
      const res = await fetch(`${API_URL}/api/admin/consultants/${selected._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setShowEdit(false);
        fetchConsultants();
        if (view === 'detail') {
          // fetch detail again to update view
          handleShowDetail(selected);
        }
      }
    } finally {
      setEditing(false);
    }
  };

  // DELETE
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/consultants/${selected._id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setShowDelete(false);
        setView('list');
        setSelected(null);
        fetchConsultants();
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <Row className="mb-3">
        <Col>
          {(view === 'list') && (
            <h4 className="mb-0">Consultants</h4>
          )}
          {(view === 'detail') && (
            <div className="d-flex align-items-center gap-2 mb-0">
              <Button
                variant="link"
                className="p-0 me-2"
                style={{ fontSize: 18, color: "#1a73e8" }}
                onClick={() => { setView('list'); setSelected(null); setApplicants([]); }}
              >
                <ArrowLeft size={22} />
              </Button>
              <h4 className="mb-0" style={{lineHeight:"1.5"}}>Consultant Details</h4>
            </div>
          )}
          {(view === 'applicant') && (
            <div className="d-flex align-items-center gap-2 mb-0">
              <Button
                variant="link"
                className="p-0 me-2"
                style={{ fontSize: 18, color: "#1a73e8" }}
                onClick={() => setView('detail')}
              >
                <ArrowLeft size={22} />
              </Button>
              <h4 className="mb-0" style={{lineHeight:"1.5"}}>Applicant Details</h4>
            </div>
          )}
        </Col>
        {view === 'list' && (
          <Col className="text-end">
            <Button onClick={() => setShowCreate(true)}>Add Consultant</Button>
          </Col>
        )}
      </Row>

      {/* Consultant List */}
      {view === 'list' && (
        <>
          {loading ? (
            <div className="py-5 text-center"><Spinner animation="border" /></div>
          ) : (
            <ListGroup>
              {consultants.map(c => (
                <ListGroup.Item
                  key={c._id}
                  action
                  onClick={() => handleShowDetail(c)}
                >
                  <b>{c.name}</b> <span className="text-muted">({c.position || c.skills})</span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </>
      )}

      {/* Consultant Detail View (with Applicants) */}
      {view === 'detail' && selected && (
        <Row className="mt-4">
          <Col md={6} className="mb-4">
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title>
                  <div className="d-flex align-items-center gap-2">
                    <span>{selected.name}</span>
                    {selected.position && <span className="badge bg-secondary ms-2">{selected.position}</span>}
                  </div>
                </Card.Title>
                <div><b>Email:</b> {selected.email}</div>
                <div><b>Skills:</b> {selected.skills}</div>
                <div><b>Rate:</b> {selected.rate}</div>
                <div><b>Availability:</b> {selected.availability}</div>
                <div><b>Projects:</b> {(selected.projects || []).join(', ')}</div>
                <h6 className="mt-3">Qualifications</h6>
                <ul className="mb-1">
                  {(selected.qualification || []).map((q, i) => (
                    <li key={i}>{q.course} from {q.university} ({q.year})</li>
                  ))}
                </ul>
                <h6>Experience</h6>
                <ul>
                  {(selected.experience || []).map((e, i) => (
                    <li key={i}>{e.role} at {e.company} ({e.from} - {e.to})</li>
                  ))}
                </ul>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-secondary" onClick={handleEditClick}>Edit</Button>
                  <Button variant="outline-danger" onClick={handleDeleteClick}>Delete</Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title>Applicants</Card.Title>
                {loadingApplicants ? (
                  <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : applicants.length === 0 ? (
                  <div className="text-muted">No applicants found.</div>
                ) : (
                  <ListGroup>
                    {applicants.map(app => (
                      <ListGroup.Item
                        key={app._id}
                        action
                        onClick={() => handleShowApplicantDetail(app)}
                      >
                        <b>{app.b2bId.first_name} {app.b2bId.last_name}</b>{" "}
                        <span className="text-muted">({app.b2bId.company_name})</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Applicant Detail View */}
      {view === 'applicant' && selectedApplicant && (
        <Row className="mt-4 justify-content-center">
          <Col md={8}>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title>
                  <div className="d-flex align-items-center gap-2">
                    <span>
                      {selectedApplicant.b2bId.first_name} {selectedApplicant.b2bId.last_name}
                    </span>
                    <span className="badge bg-info ms-2">{selectedApplicant.b2bId.role}</span>
                  </div>
                </Card.Title>
                <div><b>Email:</b> {selectedApplicant.b2bId.email}</div>
                <div><b>Company Name:</b> {selectedApplicant.b2bId.company_name}</div>
                <div><b>Phone:</b> {selectedApplicant.b2bId.phone}</div>
                <div><b>Applied At:</b> {new Date(selectedApplicant.appliedAt).toLocaleString()}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* -- Create Consultant Modal (Popup) -- */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg">
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Create Consultant</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ConsultantFormFields
              form={form}
              setForm={setForm}
              handleQualChange={handleQualChange}
              handleExpChange={handleExpChange}
              addQual={addQual}
              removeQual={removeQual}
              addExp={addExp}
              removeExp={removeExp}
              isEdit={false}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* -- Edit Consultant Modal (Popup) -- */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Consultant</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ConsultantFormFields
              form={form}
              setForm={setForm}
              handleQualChange={handleQualChange}
              handleExpChange={handleExpChange}
              addQual={addQual}
              removeQual={removeQual}
              addExp={addExp}
              removeExp={removeExp}
              isEdit={true}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={editing}>
              {editing ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* -- Delete Confirm Modal -- */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Consultant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <b>{selected && selected.name}</b>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Yes, Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Reusable form fields (for both create and edit)
function ConsultantFormFields({
  form, setForm,
  handleQualChange, handleExpChange,
  addQual, removeQual,
  addExp, removeExp,
  isEdit
}) {
  return (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-2">
          <Form.Label>Name</Form.Label>
          <Form.Control required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Email</Form.Label>
          <Form.Control required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Skills</Form.Label>
          <Form.Control required value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} placeholder="Comma separated" />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Position</Form.Label>
          <Form.Control value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Rate</Form.Label>
          <Form.Control value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Availability</Form.Label>
          <Form.Control value={form.availability} onChange={e => setForm(f => ({ ...f, availability: e.target.value }))} />
        </Form.Group>
        <Form.Label className="mt-2">Qualification(s)</Form.Label>
        {form.qualification.map((q, i) => (
          <Row className="mb-2" key={i}>
            <Col>
              <Form.Control placeholder="University" value={q.university} onChange={e => handleQualChange(i, "university", e.target.value)} />
            </Col>
            <Col>
              <Form.Control placeholder="Course" value={q.course} onChange={e => handleQualChange(i, "course", e.target.value)} />
            </Col>
            <Col>
              <Form.Control placeholder="Year" value={q.year} onChange={e => handleQualChange(i, "year", e.target.value)} />
            </Col>
            <Col xs="auto">
              <Button variant="outline-danger" size="sm" onClick={() => removeQual(i)} disabled={form.qualification.length === 1}>-</Button>
              <Button variant="outline-success" size="sm" className="ms-1" onClick={addQual}>+</Button>
            </Col>
          </Row>
        ))}
      </Col>
      <Col md={6}>
        <Form.Label className="mt-2">Experience(s)</Form.Label>
        {form.experience.map((e, i) => (
          <Row className="mb-2" key={i}>
            <Col>
              <Form.Control placeholder="Company" value={e.company} onChange={ev => handleExpChange(i, "company", ev.target.value)} />
            </Col>
            <Col>
              <Form.Control placeholder="Role" value={e.role} onChange={ev => handleExpChange(i, "role", ev.target.value)} />
            </Col>
            <Col>
              <Form.Control placeholder="From" value={e.from} onChange={ev => handleExpChange(i, "from", ev.target.value)} />
            </Col>
            <Col>
              <Form.Control placeholder="To" value={e.to} onChange={ev => handleExpChange(i, "to", ev.target.value)} />
            </Col>
            <Col xs="auto">
              <Button variant="outline-danger" size="sm" onClick={() => removeExp(i)} disabled={form.experience.length === 1}>-</Button>
              <Button variant="outline-success" size="sm" className="ms-1" onClick={addExp}>+</Button>
            </Col>
          </Row>
        ))}
        <Form.Group className="mb-2 mt-3">
          <Form.Label>Projects (comma separated or JSON array)</Form.Label>
          <Form.Control value={form.projects} onChange={e => setForm(f => ({ ...f, projects: e.target.value }))} />
        </Form.Group>
      </Col>
    </Row>
  );
}

export default ConsultantTab;
